<template>
  <div class="status-settings">
    <div class="row">
      <!-- Left Sidebar: Category List -->
      <div class="col-md-3 col-lg-2">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">
              <Tag :size="16" class="me-1" />
              Categories
            </h6>
          </div>
          <div class="list-group list-group-flush">
            <button
              v-for="category in categories"
              :key="category.value"
              class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              :class="{ active: selectedCategory === category.value }"
              @click="selectCategory(category.value)"
            >
              <span>{{ category.label }}</span>
              <span class="badge bg-secondary rounded-pill">{{ getStatusCount(category.value) }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Right Panel: Status Management -->
      <div class="col-md-9 col-lg-10">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ currentCategoryLabel }} Statuses
            </h5>
            <div class="d-flex gap-2">
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="loadStatuses"
                :disabled="loading"
                title="Refresh"
              >
                <RefreshCw :size="16" :class="{ 'fa-spin': loading }" />
              </button>
              <button class="btn btn-sm btn-primary" @click="openAddModal">
                <Plus :size="16" class="me-1" />
                Add Status
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <i class="fas fa-spinner fa-spin me-2"></i>
              Loading statuses...
            </div>
            <div v-else-if="statuses.length === 0" class="text-center py-4 text-muted">
              <AlertCircle :size="48" class="mb-2" />
              <p>No statuses found for this category.</p>
              <button class="btn btn-sm btn-outline-primary" @click="openAddModal">
                Create your first status
              </button>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th style="width: 40px">Order</th>
                    <th style="width: 60px">Value</th>
                    <th style="width: 100px">Color</th>
                    <th>Label</th>
                    <th style="width: 60px">Icon</th>
                    <th style="width: 100px">Active</th>
                    <th style="width: 120px">Entity Active</th>
                    <th style="width: 120px" class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="status in sortedStatuses"
                    :key="status.Id"
                    :class="{ 'table-secondary': !status.IsActive }"
                  >
                    <td>
                      <span class="badge bg-light text-dark">{{ status.SortOrder }}</span>
                    </td>
                    <td>
                      <span class="badge bg-primary">{{ status.Value }}</span>
                    </td>
                    <td>
                      <div
                        class="color-preview"
                        :style="{ backgroundColor: status.Color || '#6c757d' }"
                        :title="status.Color || '#6c757d'"
                      ></div>
                    </td>
                    <td>
                      <strong>{{ status.Label }}</strong>
                      <div class="small text-muted">DB ID: {{ status.Id }}</div>
                    </td>
                    <td class="text-center">
                      <i v-if="status.Icon" :class="status.Icon"></i>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="status.IsActive ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ status.IsActive ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="status.IsRefActive ? 'bg-success' : 'bg-warning'"
                        :title="status.IsRefActive ? 'Entity remains active/open' : 'Entity is terminal/closed'"
                      >
                        {{ status.IsRefActive ? 'Active' : 'Terminal' }}
                      </span>
                    </td>
                    <td class="text-end">
                      <button
                        class="btn btn-sm btn-outline-primary me-1"
                        @click="openEditModal(status)"
                        title="Edit"
                      >
                        <Edit :size="14" />
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        @click="confirmDelete(status)"
                        title="Delete"
                        :disabled="!canDelete(status)"
                      >
                        <Trash2 :size="14" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Edit Modal -->
    <StatusEditModal
      v-if="showModal"
      :visible="showModal"
      :status="editingStatus"
      :category="selectedCategory"
      @close="closeModal"
      @save="handleSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { Tag, RefreshCw, Plus, Edit, Trash2, AlertCircle } from 'lucide-vue-next';
import { useStatusStore } from '@/stores/StatusStore';
import type { StatusDefinition, SaveStatusRequest } from '@/models/StatusDefinitionModel';
import StatusEditModal from './StatusEditModal.vue';
import Alert from '@/services/Alert';

const categories = [
  { label: 'Request', value: 'Request' },
  { label: 'Work Order', value: 'WorkOrder' },
  { label: 'Part', value: 'Part' },
  { label: 'Expense', value: 'Expense' },
  { label: 'Mileage', value: 'Mileage' },
  { label: 'Time', value: 'Time' }
];

const selectedCategory = ref('Request');
const showModal = ref(false);
const editingStatus = ref<StatusDefinition | null>(null);

// Get status store
const statusStore = useStatusStore();

// Local loading state per category
const localLoading = ref<Record<string, boolean>>({});

// Computed
const currentCategoryLabel = computed(() => {
  const cat = categories.find(c => c.value === selectedCategory.value);
  return cat?.label || selectedCategory.value;
});

const statuses = computed(() => {
  return statusStore.getStatuses(selectedCategory.value);
});

const loading = computed(() => {
  return statusStore.isLoading(selectedCategory.value) || localLoading.value[selectedCategory.value];
});

const sortedStatuses = computed(() => {
  return [...statuses.value].sort((a, b) => a.SortOrder - b.SortOrder);
});

const getStatusCount = (category: string) => {
  return statusStore.getStatuses(category).length.toString();
};

// Methods
const selectCategory = async (category: string) => {
  selectedCategory.value = category;
  if (!statusStore.isLoaded(category)) {
    await loadStatuses();
  }
};

const loadStatuses = async () => {
  localLoading.value[selectedCategory.value] = true;
  await statusStore.loadStatuses(selectedCategory.value);
  localLoading.value[selectedCategory.value] = false;
};

const openAddModal = () => {
  editingStatus.value = null;
  showModal.value = true;
};

const openEditModal = (status: StatusDefinition) => {
  editingStatus.value = { ...status };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingStatus.value = null;
};

const handleSave = async (statusData: SaveStatusRequest) => {
  try {
    await statusStore.saveStatus(statusData);
    closeModal();
    Alert.notify({ text: 'Status saved successfully', type: 'success' });
  } catch (error) {
    console.error('Error saving status:', error);
    Alert.notify({ text: 'Failed to save status', type: 'error' });
  }
};

const canDelete = (status: StatusDefinition) => {
  // Prevent deletion of system-critical statuses if needed
  // For now, allow all deletions (soft delete)
  return true;
};

const confirmDelete = (status: StatusDefinition) => {
  Alert.confirm(
    `Are you sure you want to delete "${status.Label}"?`,
    async () => {
      try {
        await statusStore.deleteStatus(selectedCategory.value, status.Id);
        Alert.notify({ text: 'Status deleted successfully', type: 'success' });
      } catch (error) {
        console.error('Error deleting status:', error);
        Alert.notify({ text: 'Failed to delete status', type: 'error' });
      }
    },
    () => {} // Cancel
  );
};

// Watch for category changes and load if needed
watch(selectedCategory, async (newCategory) => {
  if (!statusStore.isLoaded(newCategory)) {
    await loadStatuses();
  }
}, { immediate: false });

onMounted(async () => {
  await loadStatuses();
});
</script>

<style scoped>
.status-settings {
  min-height: 500px;
}

.color-preview {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item.active {
  background-color: #132341;
  border-color: #132341;
}

.table-secondary {
  opacity: 0.7;
}
</style>
